import { GeckoColorScale } from 'color-scale-kit'
import { GeckoBinnedDataBuffer } from 'features/dataLayers/geckoBinnedDataBuffer'
import {
  GeckoComponentDataFrame,
  GeckoComponentModel,
  GeckoInspection,
  GeckoMeasurementUnit,
} from 'types'

import { createExcelSheet } from './ExcelUtils'

export const handleCreateExcelSheet = async (
  selectedInspection: GeckoInspection | undefined,
  selectedCDF: GeckoComponentDataFrame | undefined,
  siteName: string,
  unitName: string,
  organizationName: string,
  selectedComponentName: string,
  selectedComponentModel: GeckoComponentModel | undefined,
  setIsCreatingExcelSheet: React.Dispatch<React.SetStateAction<boolean>>,
  setToastOpen: React.Dispatch<React.SetStateAction<boolean>>,
  binnedDataBuffer: GeckoBinnedDataBuffer | undefined,
  isCDFLoading: boolean,
  colorScale: GeckoColorScale | undefined,
  getPreferredMeasurementUnit: (
    inputUnit: GeckoMeasurementUnit
  ) => GeckoMeasurementUnit
) => {
  if (
    !selectedCDF ||
    !selectedInspection ||
    !selectedComponentName ||
    !binnedDataBuffer ||
    isCDFLoading
  )
    return

  setIsCreatingExcelSheet(true)

  // This setTimeout is gross but is required to ensure the above
  // setIsCreatingExcelSheet() happens before this takes over the main
  // thread. This whole function is a good candidate for WebWorker-ization
  setTimeout(async () => {
    const source = selectedCDF.data.params.source.name
    const view = selectedCDF.data.params.view.name
    const aggregation = selectedCDF.data.params.aggregation.name
    const angle_plot_setting = selectedCDF.data.params.angle_plot_setting.id
    const entryList = [
      siteName,
      unitName,
      selectedComponentName,
      source,
      view,
      aggregation,
      angle_plot_setting,
    ]
    const excelFileName = entryList.join('_').toLowerCase()
    const formattedExcelFileName = excelFileName
      .replace(/[^0-9a-zA-Z]+/g, '_')
      .replace('.', '')

    const thicknessUnits = selectedCDF.data.params.measurement_unit
    const xAxisUnits = selectedCDF.data.geometry.x.unit
    const yAxisUnits = selectedCDF.data.geometry.y.unit

    // Make sure the units on the cdf are valid GeckoMeasurementUnits
    const inValidMeasurement = (id: string) => {
      return (
        GeckoMeasurementUnit.GetByID(id) === undefined ||
        GeckoMeasurementUnit.GetByID(id) === GeckoMeasurementUnit.None
      )
    }
    if (
      inValidMeasurement(thicknessUnits.id) ||
      inValidMeasurement(xAxisUnits.id) ||
      inValidMeasurement(yAxisUnits.id)
    ) {
      setToastOpen(true)
      setIsCreatingExcelSheet(false)
      return
    }

    // Populate the Excel sheet
    const workbook = await createExcelSheet(
      organizationName,
      siteName,
      unitName,
      selectedComponentName,
      thicknessUnits,
      xAxisUnits,
      yAxisUnits,
      getPreferredMeasurementUnit,
      selectedComponentModel,
      binnedDataBuffer,
      selectedCDF,
      selectedInspection,
      colorScale
    )

    // Convert workbook to a Blob and trigger download
    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const url = URL.createObjectURL(blob)

    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = formattedExcelFileName
    anchor.click()

    // Cleanup
    URL.revokeObjectURL(url)
    setIsCreatingExcelSheet(false)
  }, 0)
}
